"use client";

import { useEffect, useState } from "react";
import IndexBadges from "@/components/IndexBadges";

type MacdRow = {
  symbol: string;
  name: string;
  sector: string;
  close: number;
  date: string;
  hist_prev: number | null;
  hist_curr: number | null;
  bearish_days?: number | null;
  indices?: string[];
};

const formatNumber = (value: number | null | undefined, digits = 2) => {
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString(undefined, { maximumFractionDigits: digits });
};

// MACD Concept Visualization Component
function MacdVisualization() {
  return (
    <div style={{
      background: "rgba(15, 23, 42, 0.6)",
      border: "1px solid rgba(148, 163, 184, 0.2)",
      borderRadius: "12px",
      padding: "1.5rem",
      marginBottom: "2rem",
    }}>
      <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "1rem", color: "var(--blue-text)" }}>
        📈 Understanding MACD Histogram Crossover
      </h3>
      
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1.5rem" }}>
        {/* Left side - Explanation */}
        <div>
          <p style={{ color: "var(--color-text-muted)", lineHeight: 1.6, marginBottom: "1rem", fontSize: "0.9rem" }}>
            <strong style={{ color: "var(--color-text)" }}>MACD</strong> (Moving Average Convergence Divergence) measures momentum by comparing two exponential moving averages.
          </p>
          
          <div style={{ background: "rgba(59, 130, 246, 0.1)", padding: "0.75rem", borderRadius: "8px", marginBottom: "1rem", fontSize: "0.85rem" }}>
            <div style={{ marginBottom: "0.5rem" }}>
              <strong style={{ color: "var(--blue-text)" }}>MACD Line</strong>
              <div style={{ color: "var(--color-text-muted)" }}>12-day EMA − 26-day EMA</div>
            </div>
            <div style={{ marginBottom: "0.5rem" }}>
              <strong style={{ color: "var(--color-success)" }}>Signal Line</strong>
              <div style={{ color: "var(--color-text-muted)" }}>9-day EMA of MACD</div>
            </div>
            <div>
              <strong style={{ color: "#fbbf24" }}>Histogram</strong>
              <div style={{ color: "var(--color-text-muted)" }}>MACD Line − Signal Line</div>
            </div>
          </div>

          <div style={{ padding: "0.75rem", background: "rgba(34, 197, 94, 0.1)", borderLeft: "3px solid var(--color-success)", borderRadius: "4px", fontSize: "0.85rem" }}>
            <strong style={{ color: "var(--color-success)" }}>✓ Bullish Signal</strong>
            <div style={{ color: "var(--color-text-muted)", marginTop: "0.3rem" }}>Histogram turns from RED (negative) to GREEN (positive)</div>
          </div>
        </div>

        {/* Right side - Visual Chart Representation */}
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-around" }}>
          <svg width="100%" height="200" viewBox="0 0 300 180" style={{ background: "rgba(0,0,0,0.2)", borderRadius: "8px" }}>
            {/* Grid lines */}
            <line x1="0" y1="90" x2="300" y2="90" stroke="rgba(148,163,184,0.2)" strokeWidth="1" />
            
            {/* Before signal - bearish (all red/negative) */}
            <g opacity="0.6">
              <rect x="10" y="100" width="8" height="40" fill="var(--color-danger)" />
              <rect x="20" y="105" width="8" height="35" fill="var(--color-danger)" />
              <rect x="30" y="108" width="8" height="32" fill="var(--color-danger)" />
              <rect x="40" y="110" width="8" height="30" fill="var(--color-danger)" />
            </g>

            {/* Crossover point */}
            <circle cx="55" cy="90" r="4" fill="#3b82f6" />
            <text x="55" y="110" textAnchor="middle" fontSize="10" fill="var(--color-text-muted)">Turn</text>

            {/* After signal - bullish (all green/positive) */}
            <g>
              <rect x="70" y="75" width="8" height="15" fill="var(--color-success)" />
              <rect x="80" y="65" width="8" height="25" fill="var(--color-success)" />
              <rect x="90" y="50" width="8" height="40" fill="var(--color-success)" />
              <rect x="100" y="35" width="8" height="55" fill="var(--color-success)" />
              <rect x="110" y="30" width="8" height="60" fill="var(--color-success)" />
            </g>

            {/* Signals legend */}
            <text x="140" y="30" fontSize="11" fill="var(--color-danger)" fontWeight="bold">BEARISH SIGNAL</text>
            <rect x="135" y="35" width="10" height="10" fill="var(--color-danger)" />
            
            <text x="140" y="60" fontSize="11" fill="var(--color-success)" fontWeight="bold">BULLISH SIGNAL</text>
            <rect x="135" y="65" width="10" height="10" fill="var(--color-success)" />
          </svg>
          
          <div style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", marginTop: "0.5rem" }}>
            Red bars (negative) → Green bars (positive) = Momentum shift detected
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MacdScannerPage() {
  const [rows, setRows] = useState<MacdRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch("/api/outputs/scanner_macd.json");
        const json = await res.json();
        if (!res.ok) {
          throw new Error(json.error ?? json.details ?? `HTTP ${res.status}`);
        }
        setRows(Array.isArray(json.rows) ? json.rows : []);
      } catch (err) {
        setRows([]);
        setError(err instanceof Error ? err.message : "Failed to load MACD scan.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">MACD Turn Scanner</h1>
          <p className="page-subtitle">Detect momentum shifts when histogram changes from negative to positive</p>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          {/* MACD Concept Visualization */}
          <MacdVisualization />

          {/* Results Summary */}
          <div style={{
            background: "linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(37, 99, 235, 0.05))",
            border: "1px solid rgba(59, 130, 246, 0.2)",
            borderRadius: "12px",
            padding: "1.25rem",
            marginBottom: "1.5rem",
            display: "flex",
            alignItems: "center",
            gap: "1.5rem",
            flexWrap: "wrap"
          }}>
            <div style={{
              background: "rgba(59, 130, 246, 0.15)",
              borderRadius: "10px",
              padding: "0.75rem 1.25rem",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
            }}>
              <span style={{ fontSize: "1.5rem" }}>📊</span>
              <span style={{ fontWeight: 700, fontSize: "1.1rem", color: "#3b82f6" }}>
                {rows.length} Signal{rows.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: "0.95rem", marginBottom: "0.25rem" }}>Histogram Crossover Signals</div>
              <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                Shares with momentum turning positive
              </div>
            </div>
          </div>

          {/* Results Table */}
          <div className="results-table" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ 
                  background: "linear-gradient(90deg, rgba(59, 130, 246, 0.15), rgba(37, 99, 235, 0.08))",
                  borderBottom: "2px solid rgba(59, 130, 246, 0.3)",
                  position: "sticky",
                  top: 0
                }}>
                  <th style={{ textAlign: "left", padding: "1rem", color: "var(--blue-text)", fontWeight: 700, fontSize: "0.9rem" }}>Symbol</th>
                  <th style={{ textAlign: "left", padding: "1rem", color: "var(--blue-text)", fontWeight: 700, fontSize: "0.9rem" }}>Company</th>
                  <th style={{ textAlign: "left", padding: "1rem", color: "var(--blue-text)", fontWeight: 700, fontSize: "0.9rem" }}>Sector</th>
                  <th style={{ textAlign: "right", padding: "1rem", color: "var(--blue-text)", fontWeight: 700, fontSize: "0.9rem" }}>Close Price</th>
                  <th style={{ textAlign: "center", padding: "1rem", color: "var(--blue-text)", fontWeight: 700, fontSize: "0.9rem" }}>Bearish Bars</th>
                  <th style={{ textAlign: "center", padding: "1rem", color: "var(--blue-text)", fontWeight: 700, fontSize: "0.9rem" }}>Histogram Shift</th>
                  <th style={{ textAlign: "right", padding: "1rem", color: "var(--blue-text)", fontWeight: 700, fontSize: "0.9rem", whiteSpace: "nowrap", minWidth: "120px" }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} style={{ padding: "2rem 1rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                      <div style={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>⏳ Loading MACD scan...</div>
                      <div style={{ fontSize: "0.85rem" }}>Analyzing {rows.length === 0 ? "all" : rows.length} shares</div>
                    </td>
                  </tr>
                )}
                {!loading && error && (
                  <tr>
                    <td colSpan={7} style={{ padding: "2rem 1rem", textAlign: "center", color: "#fca5a5" }}>
                      {error}
                    </td>
                  </tr>
                )}
                {!loading && !error && rows.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: "2.5rem 1rem", textAlign: "center" }}>
                      <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📭</div>
                      <div style={{ color: "var(--color-text-muted)", fontSize: "0.95rem" }}>
                        No MACD histogram crossover signals found.
                      </div>
                      <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", marginTop: "0.5rem" }}>
                        Try adjusting the period parameters to find more opportunities.
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && rows.map((r, idx) => {
                  const histPrev = typeof r.hist_prev === "number" ? r.hist_prev : Number(r.hist_prev);
                  const histCurr = typeof r.hist_curr === "number" ? r.hist_curr : Number(r.hist_curr);
                  const hasHist = Number.isFinite(histPrev) && Number.isFinite(histCurr);
                  const histChange = hasHist ? histCurr - histPrev : 0;
                  const isBullish = hasHist ? histChange > 0 : false;
                  
                  return (
                    <tr 
                      key={`${r.symbol}-${idx}`} 
                      style={{ 
                        borderBottom: "1px solid rgba(148, 163, 184, 0.1)",
                        background: idx % 2 === 0 ? "rgba(15, 23, 42, 0)" : "rgba(59, 130, 246, 0.03)",
                        transition: "all 0.2s ease",
                        cursor: "pointer"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(59, 130, 246, 0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = idx % 2 === 0 ? "rgba(15, 23, 42, 0)" : "rgba(59, 130, 246, 0.03)";
                      }}
                    >
                      {/* Symbol */}
                      <td style={{ padding: "1rem", fontWeight: 700, fontSize: "1rem" }}>
                        <span style={{
                          color: "var(--blue-text)",
                          padding: "0.35rem 0.65rem",
                          background: "rgba(59, 130, 246, 0.15)",
                          borderRadius: "6px",
                          display: "inline-block"
                        }}>
                          {r.symbol}
                        </span>
                        <IndexBadges indices={r.indices ?? []} style={{ marginLeft: 5, verticalAlign: "middle" }} />
                      </td>
                      
                      {/* Company */}
                      <td style={{ padding: "1rem", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
                        {r.name}
                      </td>
                      
                      {/* Sector */}
                      <td style={{ padding: "1rem" }}>
                        <span style={{
                          padding: "0.35rem 0.65rem",
                          background: "rgba(148, 163, 184, 0.1)",
                          borderRadius: "6px",
                          color: "var(--color-text-muted)",
                          fontSize: "0.85rem"
                        }}>
                          {r.sector}
                        </span>
                      </td>
                      
                  {/* Close Price */}
                  <td style={{ padding: "1rem", textAlign: "right", fontWeight: 600, fontSize: "0.95rem" }}>
                    {formatNumber(r.close)}
                  </td>

                  {/* Bearish Bars */}
                  <td style={{ padding: "1rem", textAlign: "center", fontWeight: 600, fontSize: "0.9rem" }}>
                    {typeof r.bearish_days === "number" ? r.bearish_days.toLocaleString() : "—"}
                  </td>
                  
                  {/* Histogram Shift - Visual Representation */}
                  <td style={{ padding: "1rem", textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem", minWidth: "280px" }}>
                          {/* Previous Value */}
                          <div style={{ textAlign: "right", minWidth: "60px" }}>
                            <div style={{
                              color: hasHist && histPrev < 0 ? "var(--color-danger)" : "var(--blue-text)",
                              fontWeight: 500,
                              fontSize: "0.85rem"
                            }}>
                              {formatNumber(histPrev, 3)}
                            </div>
                            <div style={{ fontSize: "0.7rem", color: "var(--color-text-muted)", marginTop: "0.15rem" }}>prev</div>
                          </div>
                          
                          {/* Arrow and Visual Bar */}
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <span style={{ color: "var(--color-text-muted)", fontSize: "1rem" }}>→</span>
                            
                            {/* Visual histogram bar */}
                            <div style={{
                              display: "flex",
                              alignItems: "flex-end",
                              gap: "2px",
                              height: "30px",
                              minWidth: "16px"
                            }}>
                              <div style={{
                                width: "6px",
                                height: `${hasHist ? Math.abs(histPrev) * 20 : 0}px`,
                                background: hasHist && histPrev < 0 ? "rgba(248, 113, 113, 0.6)" : "rgba(96, 165, 250, 0.6)",
                                borderRadius: "2px",
                                transition: "all 0.2s"
                              }} />
                              <div style={{
                                width: "6px",
                                height: `${hasHist ? Math.abs(histCurr) * 20 : 0}px`,
                                background: hasHist && histCurr < 0 ? "rgba(248, 113, 113, 1)" : "rgba(16, 185, 129, 1)",
                                borderRadius: "2px",
                                transition: "all 0.2s"
                              }} />
                            </div>
                          </div>
                          
                          {/* Current Value */}
                          <div style={{ textAlign: "left", minWidth: "60px" }}>
                            <div style={{
                              color: hasHist ? (isBullish ? "var(--color-success)" : "var(--color-danger)") : "var(--color-text-muted)",
                              fontWeight: 700,
                              fontSize: "0.85rem"
                            }}>
                              {formatNumber(histCurr, 3)}
                            </div>
                            <div style={{ fontSize: "0.7rem", color: "var(--color-text-muted)", marginTop: "0.15rem" }}>now</div>
                          </div>
                          
                          {/* Change Badge */}
                          <span style={{
                            padding: "0.35rem 0.65rem",
                            background: hasHist
                              ? (isBullish ? "rgba(34, 197, 94, 0.15)" : "rgba(248, 113, 113, 0.15)")
                              : "rgba(148, 163, 184, 0.15)",
                            color: hasHist ? (isBullish ? "var(--color-success)" : "var(--color-danger)") : "var(--color-text-muted)",
                            borderRadius: "6px",
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            whiteSpace: "nowrap"
                          }}>
                            {hasHist ? (isBullish ? '✓ Bullish' : '✗ Bearish') : '—'}
                          </span>
                        </div>
                      </td>
                      
                      {/* Date */}
                  <td style={{ padding: "1rem", textAlign: "right", color: "var(--color-text-muted)", fontSize: "0.85rem", whiteSpace: "nowrap", minWidth: "120px" }}>
                    {r.date}
                  </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
